/**
 * Utility functions for formatting age ranges with proper units (months vs years)
 */

export interface FormattedAgeRange {
  value: string;
  unit: 'months' | 'years';
  unitText: string;
}

/**
 * Determines if an age range should be displayed in months or years
 * and returns the formatted result.
 * 
 * @param ageRange - Age range string like "6-9", "0-1", "3-4", etc.
 * @param lang - Language code for localization (en, es, fr, de)
 * @returns Formatted age range with value, unit, and localized unit text
 * 
 * @example
 * formatAgeRange("6-9", "en") // { value: "6-9", unit: "months", unitText: "months" }
 * formatAgeRange("3-4", "en") // { value: "3-4", unit: "years", unitText: "years" }
 * formatAgeRange("0-1", "es") // { value: "0-1", unit: "years", unitText: "años" }
 */
export function formatAgeRange(ageRange: string, lang: string = 'en'): FormattedAgeRange {
  if (!ageRange) {
    return { value: '', unit: 'years', unitText: '' };
  }
  
  // Parse the age range (e.g., "6-9", "0-1", "3-4")
  const parts = ageRange.split('-');
  if (parts.length !== 2) {
    // Default to years if format is unexpected
    return { 
      value: ageRange, 
      unit: 'years', 
      unitText: getUnitText('years', lang)
    };
  }
  
  const [start, end] = parts.map(num => parseInt(num.trim()));
  
  // Validate numbers
  if (isNaN(start) || isNaN(end)) {
    return { 
      value: ageRange, 
      unit: 'years', 
      unitText: getUnitText('years', lang)
    };
  }
  
  // IMPROVED LOGIC: More precise classification (matches age-range-utils.ts)
  let unit: 'months' | 'years' = 'years';
  const span = end - start;
  
  // RULE 1: If max age <= 2, it's ALWAYS years (0-1, 1-2, 0-2)
  if (end <= 2) {
    unit = 'years';
  }
  // RULE 2: If starts at 0 and ends <= 12, it's MONTHS (0-3, 0-5, 0-6, 0-12)
  else if (start === 0 && end <= 12) {
    unit = 'months';
  }
  // RULE 3: If both are in 6-24 range with small span, it's MONTHS (6-8, 6-9, 6-12, 9-12, 12-18, 18-24)
  else if (start >= 6 && end <= 24 && span <= 12) {
    unit = 'months';
  }
  // RULE 4: If starts at 3+ and span <= 4, it's YEARS (3-4, 5-6, 7-8, 9-10, 11-12)
  else if (start >= 3 && span <= 4) {
    unit = 'years';
  }
  // RULE 5: Edge case - 2-3 could be years (toddler transition)
  else if (start === 2 && end === 3) {
    unit = 'years';
  }
  // DEFAULT: years for safety
  else {
    unit = 'years';
  }
  
  return {
    value: ageRange,
    unit,
    unitText: getUnitText(unit, lang)
  };
}

/**
 * Get localized unit text for months or years
 */
function getUnitText(unit: 'months' | 'years', lang: string): string {
  const translations: Record<string, Record<'months' | 'years', string>> = {
    en: { months: 'months', years: 'years' },
    es: { months: 'meses', years: 'años' },
    fr: { months: 'mois', years: 'ans' },
    de: { months: 'Monate', years: 'Jahre' },
    it: { months: 'mesi', years: 'anni' },
    pt: { months: 'meses', years: 'anos' },
    nl: { months: 'maanden', years: 'jaar' },
    pl: { months: 'miesiące', years: 'lata' },
  };
  
  return translations[lang]?.[unit] || translations.en[unit];
}

